import { format } from "date-fns";

import { formatIDR } from "../../../utils/helpers/formatIDR";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import { calculatePurchaseBaseQuantity } from "../../../utils/helpers/purchaseCalculations";
import { getBaseUnitLabel, getPriceUnitLabel } from "../../../utils/helpers/pricing";
import ItemAvatar from "../../../components/ItemAvatar";
import "../FormPurchase/style.css";

const ITEM_TYPE_LABELS = {
  material: "Material",
  asset: "Aset",
  expense: "Biaya",
};

const PAYMENT_METHOD_LABELS = {
  cash: "Tunai",
  transfer: "Transfer",
  qris: "QRIS",
};

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 3,
  });

const formatDate = (value, pattern = "dd MMMM yyyy") => {
  if (!value) return "-";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : format(date, pattern);
};

const DetailPurchase = ({ formState, units }) => {
  const optionUnit = toUnitOptions(units);
  const isCancelled = formState?.payment_status === "cancelled";
  const isPaid = !isCancelled && (formState?.payment_status === "paid" || Number(formState?.due || 0) <= 0);
  const status = isCancelled
    ? { label: "Dibatalkan", className: "bg-secondary-lt text-secondary" }
    : isPaid
      ? { label: "Lunas", className: "bg-primary-lt" }
      : { label: "Belum lunas", className: "bg-warning-lt" };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-form-detail"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-detail-title"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <div className="modal-dialog modal-dialog-centered modal-xl purchase-modal-dialog" role="document">
        <div className="modal-content purchase-modal-content">
          <div className="modal-header purchase-modal-header">
            <div className="d-flex align-items-center gap-3">
              <span className="purchase-modal-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 4h14l1 4H4zM5 8v12h14V8M8 12h8M8 16h5" />
                </svg>
              </span>
              <div>
                <div className="text-secondary small fw-semibold text-uppercase">Hanya lihat</div>
                <h5 className="modal-title mb-0" id="purchase-detail-title">
                  Detail Pembelian #{formState?.id || "-"}
                </h5>
              </div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Tutup detail pembelian" />
          </div>

          <div className="modal-body purchase-modal-body">
            <section className="purchase-detail-hero">
              <div>
                <span>Total tagihan</span>
                <strong>{formatIDR(formState?.total || 0)}</strong>
                <small>{formatDate(formState?.purchase_date)}</small>
              </div>
              <div className="purchase-detail-payment-grid">
                <div>
                  <span>Dibayar</span>
                  <strong>{formatIDR(formState?.pay || 0)}</strong>
                </div>
                <div>
                  <span>Sisa hutang</span>
                  <strong>{formatIDR(formState?.due || 0)}</strong>
                </div>
                <span className={`badge ${status.className}`}>{status.label}</span>
              </div>
            </section>

            <div className="row g-4 mt-0">
              <div className="col-lg-4">
                <section className="purchase-section h-100">
                  <div className="purchase-section-heading compact">
                    <div>
                      <div className="purchase-step">01</div>
                      <div>
                        <h3>Informasi transaksi</h3>
                        <p>Referensi pembelian dari supplier.</p>
                      </div>
                    </div>
                  </div>

                  <dl className="purchase-detail-list">
                    <div>
                      <dt>Supplier / vendor</dt>
                      <dd>{formState?.supplier_name || "Tanpa supplier"}</dd>
                    </div>
                    <div>
                      <dt>Metode pembayaran</dt>
                      <dd>{PAYMENT_METHOD_LABELS[formState?.payment_method] || formState?.payment_method || "-"}</dd>
                    </div>
                    <div>
                      <dt>Tanggal pembelian</dt>
                      <dd>{formatDate(formState?.purchase_date)}</dd>
                    </div>
                    <div>
                      <dt>Dibuat</dt>
                      <dd>{formatDate(formState?.created_at, "dd MMM yyyy, HH:mm")}</dd>
                    </div>
                    {formState?.updated_at ? (
                      <div>
                        <dt>Terakhir diubah</dt>
                        <dd>{formatDate(formState.updated_at, "dd MMM yyyy, HH:mm")}</dd>
                      </div>
                    ) : null}
                  </dl>

                  {formState?.note ? (
                    <div className="purchase-detail-note">
                      <span>Catatan</span>
                      <p>{formState.note}</p>
                    </div>
                  ) : null}
                  {formState?.cancellation_reason ? (
                    <div className="purchase-detail-note border-danger bg-danger-lt">
                      <span className="text-danger">Alasan pembatalan</span>
                      <p>{formState.cancellation_reason}</p>
                      {formState?.cancelled_at ? (
                        <small className="text-secondary">
                          Dibatalkan {formatDate(formState.cancelled_at, "dd MMM yyyy, HH:mm")}
                        </small>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              </div>

              <div className="col-lg-8">
                <section className="purchase-section h-100">
                  <div className="purchase-section-heading compact">
                    <div>
                      <div className="purchase-step">02</div>
                      <div>
                        <h3>Rincian item</h3>
                        <p>Nilai di bawah adalah snapshot transaksi dan tidak dapat diedit dari halaman detail.</p>
                      </div>
                    </div>
                  </div>

                  <div className="purchase-detail-items">
                    {(formState?.details || []).length > 0 ? (
                      formState.details.map((item, index) => {
                        const itemType = item?.item_type || (item?.is_consumable ? "expense" : "material");
                        const baseQuantity = calculatePurchaseBaseQuantity(item, optionUnit);
                        const baseUnitLabel = getBaseUnitLabel(optionUnit, item?.unit);
                        const priceUnitLabel = getPriceUnitLabel(optionUnit, item?.unit);

                        return (
                          <article className="purchase-detail-item" key={item?.id || `${item?.material_name}-${index}`}>
                            <div className="purchase-detail-item-main">
                              <ItemAvatar
                                name={item?.material_name || "Item"}
                                image={item?.image_path || item?.image}
                                size="md"
                              />
                              <div>
                                <strong>{item?.material_name || "Item tanpa nama"}</strong>
                                <span>
                                  #{String(index + 1).padStart(2, "0")} · {ITEM_TYPE_LABELS[itemType] || "Item"}
                                </span>
                              </div>
                            </div>
                            <div>
                              <span>Jumlah beli</span>
                              <strong>
                                {formatQuantity(item?.qty)} {item?.unit || "unit"}
                              </strong>
                            </div>
                            <div>
                              <span>{itemType === "material" ? "Masuk stok" : "Dampak stok"}</span>
                              <strong>
                                {itemType === "material"
                                  ? `${formatQuantity(baseQuantity)} ${baseUnitLabel}`
                                  : "Tidak ada"}
                              </strong>
                            </div>
                            <div>
                              <span>Harga / {priceUnitLabel}</span>
                              <strong>{formatIDR(item?.unit_cost || 0)}</strong>
                            </div>
                            <div className="purchase-detail-item-total">
                              <span>Subtotal</span>
                              <strong>{formatIDR(item?.sub_total || 0)}</strong>
                            </div>
                          </article>
                        );
                      })
                    ) : (
                      <div className="purchase-empty-state">
                        <strong>Tidak ada rincian item</strong>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>

          <div className="modal-footer purchase-modal-footer">
            <div className="purchase-footer-summary">
              <span>{formState?.details?.length || 0} item</span>
              <strong>{formatIDR(formState?.total || 0)}</strong>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button type="button" className="btn btn-ghost-secondary" data-bs-dismiss="modal">
                Tutup
              </button>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7 9V4h10v5M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v7H7z" />
                </svg>
                Cetak
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPurchase;
